<template>
    <modal
        :show.sync="showModal"
        dismissable
        body-classes="p-0"
        :modal-classes="`modal-xl ${$style.modalClasses}`"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <div class="flex-between">
                    <h2 class="modal-title">
                        {{ title }}
                        <span
                            v-if="formattedQuoteTotal"
                            :class="$style.quoteTotal"
                        >
                            - {{ formattedQuoteTotal }}
                        </span>
                    </h2>
                    <div class="flex-end">
                        <button
                            type="button"
                            class="close"
                            aria-label="Close"
                            @click="reset()"
                        >
                            <i class="tim-icons icon-simple-remove" />
                        </button>
                    </div>
                </div>
                <div class="mt-2">
                    <p><strong>Created:</strong> {{ createdDate }}</p>
                    <span
                        v-if="quote.status"
                        :class="getStyleForStatus(quote.status)"
                    >
                        {{ quote.status || "--" }}
                    </span>
                    <p v-else>Status: --</p>
                </div>
            </template>
            <div :class="$style.header">
                <div :class="$style.customer">
                    <div :class="$style.label">Customer</div>
                    <div :class="$style.title">
                        {{ customerName }}
                    </div>
                </div>
                <div :class="$style.designer">
                    <div :class="$style.label">Designer</div>
                    <div :class="$style.title">
                        {{ designerName }}
                    </div>
                </div>
            </div>
            <el-divider content-position="left">Details</el-divider>
            <form
                class="mt-3"
                autocomplete="off"
                @submit.prevent="validate().then(updateQuote)"
            >
                <div class="row">
                    <div class="col-4">
                        <base-input
                            name="estimatedReceiptDate"
                            type="date"
                            label="Estimated Receipt Date"
                        >
                            <el-date-picker
                                :value="quote.estimatedReceiptDate"
                                format="MM/dd/yyyy"
                                type="date"
                                placeholder="--"
                                disabled
                            />
                        </base-input>
                    </div>
                    <div class="col-4">
                        <base-input
                            name="estimatedDeliveryDate"
                            type="date"
                            label="Estimated Delivery Date"
                        >
                            <el-date-picker
                                :value="quote.estimatedDeliveryDate"
                                format="MM/dd/yyyy"
                                type="date"
                                placeholder="--"
                                disabled
                            />
                        </base-input>
                    </div>
                </div>
                <div class="row">
                    <div class="col-3">
                        <base-input
                            :value="quote.locationName"
                            name="location"
                            type="text"
                            label="Location Name"
                            disabled
                        />
                    </div>
                    <div class="col-3">
                        <base-input
                            :value="quote.deliveryAddress"
                            name="address"
                            type="text"
                            label="Delivery Address"
                            disabled
                        />
                    </div>
                    <div class="col-3">
                        <base-input
                            :value="quote.deliveryCity"
                            name="city"
                            type="text"
                            label="Delivery City"
                            disabled
                        />
                    </div>
                    <div class="col-3">
                        <base-input
                            :value="quote.deliveryZip"
                            label="Delivery Zip"
                            name="zip"
                            autocomplete="off"
                            type="text"
                            disabled
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-4">
                        <base-input
                            :value="quote.contact"
                            name="contactName"
                            type="text"
                            label="Contact Name"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            :value="quote.phone"
                            label="Contact Phone"
                            name="contactPhone"
                            type="text"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            :value="quote.email"
                            label="Contact Email"
                            name="contactEmail"
                            type="text"
                            disabled
                        />
                    </div>
                </div>
                <div>
                    <base-input name="quoteNotes" label="Quote Notes">
                        <textarea
                            v-focus
                            :value="quote.notes"
                            style="max-height: unset"
                            class="form-control"
                            rows="4"
                            disabled
                        />
                    </base-input>
                </div>
            </form>
            <div v-if="quote.materials.length" class="mt-5">
                <div :class="$style.sectionTitle">Materials</div>
                <el-table max-height="350" :data="quote.materials">
                    <el-table-column
                        v-for="column in materialColumns"
                        :key="column.label"
                        :width="column.width"
                        :min-width="column.minWidth"
                        :prop="column.prop"
                        :label="column.label"
                        :formatter="column.formatter"
                        :show-overflow-tooltip="true"
                    />
                </el-table>
            </div>
            <div v-if="quote.details.length">
                <div :class="$style.sectionTitle">Itemized Details</div>
                <el-table
                    max-height="350"
                    show-summary
                    :summary-method="getSummaries"
                    :data="quote.details"
                >
                    <el-table-column
                        v-for="column in detailColumns"
                        :key="column.label"
                        :width="column.width"
                        :min-width="column.minWidth"
                        :prop="column.prop"
                        :label="column.label"
                        :formatter="column.formatter"
                        :show-overflow-tooltip="true"
                    />
                </el-table>
            </div>
            <div :class="$style.actions">
                <base-button type="default" class="btn-fill" @click="reset()">
                    Close
                </base-button>
                <div :class="$style.right">
                    <el-tooltip
                        :disabled="deleteButtonEnabled"
                        :open-delay="300"
                        content="You may only delete a quote that has a status of Submitted."
                        placement="top"
                    >
                        <div class="d-inline-flex">
                            <base-button
                                :disabled="!deleteButtonEnabled"
                                type="primary"
                                class="btn-fill"
                                @click="$emit('deleteQuote')"
                            >
                                Delete
                            </base-button>
                        </div>
                    </el-tooltip>
                    <el-tooltip
                        :disabled="declineButtonEnabled"
                        :open-delay="300"
                        content="You may only decline a quote that is pending."
                        placement="top"
                    >
                        <div class="d-inline-flex">
                            <base-button
                                :disabled="!declineButtonEnabled"
                                type="primary"
                                class="btn-fill"
                                @click="$emit('declineQuote')"
                            >
                                Decline
                            </base-button>
                        </div>
                    </el-tooltip>
                    <el-tooltip
                        :disabled="approveButtonEnabled"
                        :open-delay="300"
                        content="You may only approve a quote that is pending."
                        placement="top"
                    >
                        <div class="d-inline-flex">
                            <base-button
                                :disabled="!approveButtonEnabled"
                                type="success"
                                class="btn-fill"
                                @click="$emit('approveQuote')"
                            >
                                Approve
                            </base-button>
                        </div>
                    </el-tooltip>
                </div>
            </div>
        </card>
    </modal>
</template>

<script>
import moment from "moment-timezone";
import { Modal } from "src/components/UI";
import { DatePicker, Divider } from "element-ui";

export default {
    name: "QuoteDetail",
    components: {
        Modal,
        [DatePicker.name]: DatePicker,
        [Divider.name]: Divider,
    },
    props: {
        quote: {
            type: Object,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
        deleteButtonEnabled: {
            type: Boolean,
            required: true,
        },
        approveButtonEnabled: {
            type: Boolean,
            required: true,
        },
        declineButtonEnabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            showModal: false,
            showPayQuoteModal: false,
            materialColumns: [
                {
                    prop: "receiptLog.id",
                    label: "Receipt Log ID",
                },
                {
                    prop: "receiptLog.customerJobId",
                    label: "Job ID",
                },
                {
                    prop: "materialType.description",
                    label: "Description",
                },
                {
                    prop: "unitCount",
                    label: "Qty",
                },
                {
                    prop: "unitSize",
                    label: "Type",
                },
            ],
            detailColumns: [
                {
                    prop: "description",
                    label: "Description",
                },
                {
                    prop: "unitCount",
                    label: "Units",
                    width: 100,
                },
                {
                    prop: "rate",
                    label: "Rate",
                    formatter: (row) => this.formatCurrency(row.rate),
                    width: 150,
                },
                {
                    prop: "billableAmount",
                    label: "Amount",
                    width: 150,
                    formatter: (row) => this.formatCurrency(row.billableAmount),
                },
            ],
        };
    },
    computed: {
        title() {
            return `Quote #${this.quote.id}`;
        },
        createdDate() {
            if (this.quote.date) {
                return moment(this.quote.date).format("MM/DD/YYYY");
            }
            return "--";
        },
        customerName() {
            return this.quote.customer?.name || "--";
        },
        designerName() {
            return this.quote.designer?.fullName || "--";
        },
        quoteTotal() {
            return this.quote.total;
        },
        formattedQuoteTotal() {
            return this.formatCurrency(this.quoteTotal);
        },
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                this.showModal = val;
            },
        },
        showModal(val) {
            this.$emit("update:show", val);
        },
    },
    methods: {
        getStyleForStatus(status) {
            if (!status) return "";
            return [status.toLowerCase(), "statusLabel"];
        },
        getSummaries() {
            return ["Total", null, null, this.formattedQuoteTotal];
        },
        formatCurrency(value) {
            const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            });
            return formatter.format(value);
        },
        reset() {
            this.showModal = false;
            this.$emit("cancel");
        },
    },
};
</script>

<style module lang="scss">
@import "assets/sass/dashboard/custom/_variables.scss";
.modalClasses {
    min-width: 750px;
}
.header {
    display: flex;
    padding-left: 10px;
    padding-right: 30px;
    .title {
        font-size: 18px;
        color: $black;
        font-weight: 600;
    }
    .customer {
        flex-basis: 70%;
    }
}
.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}
.actions .right > * {
    margin-right: 10px;
    margin-left: 10px;
}
.link {
    font-weight: 400;
    padding: 0px;
    margin: 0px;
    margin-left: 3px;
    line-height: 1.5;
}
.quoteTotal {
    margin-left: 5px;
    color: #304067;
}
.sectionTitle {
    font-size: 18px;
    font-weight: 500;
    color: #303133;
}
</style>
